import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, filter, first, map, Observable, of, Subject, switchMap } from 'rxjs';
import * as config from '../shared/config/app.config';
import { B2BUnitModel, DefaultB2BUnit } from '../shared/models/user.model';
import { BaseService } from '../shared/services/base.service';
import { PaymentInfoResponse } from "../shared/models/payment-info.model";
import { B2bUserService } from "../shared/services/b2b-user.service";
import { toObservable } from "@angular/core/rxjs-interop";
import { UserAccountFacade } from "@spartacus/user/account/root";
import { CsAgentAuthService } from "@spartacus/asm/root";

@Injectable({
  providedIn: 'root',
})
export class CustomProfileAndPreferencesService {
  private readonly b2bUserService = inject(B2bUserService);
  private readonly user = inject(UserAccountFacade);
  private readonly csAgentAuthService = inject(CsAgentAuthService);
  userData: any;
  userDataUpdate: any;
  userType: any;
  userRoles: any;
  passQues: any;
  iframeUrl: any;
  userAccountData: BehaviorSubject<DefaultB2BUnit>;
  userAccountDetails: any;
  userAccountType: any;
  userAccountdetailsType: any;
  cardDetails: Subject<PaymentInfoResponse>;
  deleteCard: any;
  updateCardDetails: any;
  profileAndPrefrencesIsActive: any;
  private userAccountIdDetails = new BehaviorSubject<any>(null);
  private $b2bUnit: Observable<any>;
  userAccountIdDetails$ = this.userAccountIdDetails.asObservable();

  constructor(private readonly baseService: BaseService) {
    this.userDataUpdate = new Subject();
    this.userAccountData = new BehaviorSubject<DefaultB2BUnit>(null);
    this.userAccountDetails = new Subject();
    this.userData = new Subject();
    this.userRoles = new Subject();
    this.passQues = new Subject();
    this.iframeUrl = new Subject();
    this.cardDetails = new Subject();
    this.deleteCard = new Subject();
    this.updateCardDetails = new Subject();
    this.profileAndPrefrencesIsActive = new Subject();
    this.$b2bUnit = toObservable(this.b2bUserService.b2bUser);
  }

  public fetchUserData(): Observable<any> {
    const userId = localStorage.getItem('asmUserId');
    const asmEnabled = localStorage.getItem('asmEnabled');
    let currentUser;
    if (asmEnabled == 'false') {
      currentUser = config.USER_NAME;
    } else {
      currentUser = 'users/' + userId;
    }
    return this.baseService.get(currentUser);
  }

  public getUserDataFromDb(idNo) {
    this.fetchUserData().subscribe((resp) => {
      if (resp !== null && resp !== undefined && resp?.length !== 0) {
        this.userType = resp.type;
        this.userData.next({ res: resp, id: idNo });
      }
    });
  }

  public updateUserDataRequest(params): Observable<any> {
    return this.baseService.patch(config.USER_NAME, params).pipe(
      filter((res) => res === null || res === undefined),
      map(() => true),
      catchError((err) => of(err))
    );
  }

  public updateUserData(params) {
    this.updateUserDataRequest(params).subscribe((result) => this.userDataUpdate.next(result));
  }

  public getUserAccountDataFromDb(userId): void {
    this.baseService
      .get(`${ config.USER_ACCOUNT }${ userId }/b2bUnits/`, { fields: 'FULL' })
      .subscribe((res: B2BUnitModel) => {
        const { defaultB2BUnit } = res;
        this.userAccountData.next(defaultB2BUnit);
      });
  }

  public getUserAccountDetailsDataFromDb(userId, accountId): void {
    this.baseService
      .get(`${ config.USER_ACCOUNT }${ userId }/b2bUnits/${ accountId }`, {
        fields: 'FULL',
      })
      .subscribe((res) => {
        if (res !== null && res !== undefined && res?.length !== 0) {
          this.userAccountdetailsType = res.type;
          this.userAccountDetails.next({ res });
        }
      });
  }

  public getHintQuestions() {
    this.baseService
      .get('/enumeration?enumerationType=HintQuestions')
      .subscribe((res) => {
        if (res !== null && res !== undefined && res?.length !== 0) {
          this.passQues.next(res);
        }
      });
  }

  public getUserRoles() {
    this.baseService
      .get('/enumeration?enumerationType=UserRole')
      .subscribe((res) => {
        if (res !== null && res !== undefined && res?.length !== 0) {
          this.userRoles.next(res);
        }
      });
  }

  public getIframeUrl(forCheckingAccounts: boolean) {
    combineLatest([
      this.csAgentAuthService.isCustomerEmulated(),
      this.user.get()
    ]).pipe(
      first(([isCustomerEmulated, user]) => !!user),
      switchMap(([isCustomerEmulated, user]) => {
        return this.baseService.get(this.getUrlBasedOnUser(isCustomerEmulated, forCheckingAccounts, user.uid));
      }),
      first(Boolean)
    ).subscribe((res) => {
      this.iframeUrl.next(res);
    })
  }

  public getCardFromDb(id) {
    this.baseService
      .get(`${ config.GET_CARD }accountId=${ id }`)
      .subscribe((res) => {
        if (res !== null && res !== undefined && res?.length !== 0) {
          this.cardDetails.next(res);
        }
      });
  }

  public getAccountIdFromDb() {
    this.userAccountIdDetails.next(null);
    this.$b2bUnit
      .pipe(
        first(Boolean)
      )
      .subscribe((res) => {
        this.userAccountIdDetails.next(res);
      })
  }

  public deleteCardFromDb(paymentdetailsId): void {
    this.baseService
      .delete(
        `${ config.USER_ACCOUNT }current/paymentdetails/${ paymentdetailsId }`,
        { fields: 'FULL' }
      )
      .subscribe((res) => {
        if (res !== null && res !== undefined && res?.length !== 0) {
          this.deleteCard.next(res);
        }
      });
  }

  public editCardDetails(paymentdetailsId, request): void {
    this.baseService
      .patch(
        `${ config.USER_ACCOUNT }current/paymentdetails/${ paymentdetailsId }`,
        request
      )
      .subscribe((res) => {
        if (res !== null && res !== undefined && res?.length !== 0) {
          this.updateCardDetails.next(res);
        }
      });
  }

  private getUrlBasedOnUser(isCustomerEmulated: boolean, forCheckingAccounts: boolean, userId: string): string {
    if (isCustomerEmulated) {
      return forCheckingAccounts ? `/users/${ userId }/paymetric/get-iframe-ach` : `/users/${ userId }/paymetric/get-iframe`;
    }

    return forCheckingAccounts ? `/users/current/paymetric/get-iframe-ach` : `/users/current/paymetric/get-iframe`;
  }
}
